<template>
  <a-modal
    title="事件详情"
    :visible="visible"
    :width="700"
    :footer="null"
    @cancel="onCancel"
    :bodyStyle="{ padding: '24px' }">
    
    <a-spin :spinning="loading">
      <div v-if="eventRecord" class="event-detail">
        <!-- 状态卡片 -->
        <div class="status-card">
          <div class="status-header">
            <span class="status-label">当前状态</span>
            <span :class="['status-badge', getStatusClass(eventRecord)]">
              {{ getStatusText(eventRecord) }}
            </span>
          </div>
          <div class="status-meta">
            <span class="meta-item">
              <span class="meta-label">事件类型</span>
              <span class="meta-value">{{ (eventRecord.event_type && eventRecord.event_type.name) || '-' }}</span>
            </span>
            <span class="meta-item">
              <span class="meta-label">创建时间</span>
              <span class="meta-value">{{ eventRecord.created_at }}</span>
            </span>
          </div>
        </div>

        <!-- 详细信息 -->
        <div class="info-section">
          <div class="info-row">
            <span class="info-label">事件ID</span>
            <span class="info-value">{{ eventRecord.id }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">是否需要处理</span>
            <span :class="['info-value', eventRecord.should_handle === 1 ? 'text-warning' : 'text-muted']">
              {{ eventRecord.should_handle === 1 ? '需处理' : '无需处理' }}
            </span>
          </div>
          <div class="info-row">
            <span class="info-label">描述</span>
            <span class="info-value">{{ eventRecord.description || '-' }}</span>
          </div>
        </div>

        <!-- 处理信息（仅需要处理的事件显示） -->
        <div v-if="eventRecord.should_handle === 1" class="process-section">
          <div class="section-title">处理信息</div>
          <div class="info-row">
            <span class="info-label">提交人</span>
            <span class="info-value">{{ eventRecord.submitter_name || '-' }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">验收人</span>
            <span class="info-value">{{ eventRecord.reviewer_name || '-' }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">完成时间</span>
            <span class="info-value">{{ eventRecord.finished_at || '-' }}</span>
          </div>
        </div>

        <!-- 操作按钮 -->
        <div v-if="canOperate" class="action-section">
          <a-button 
            v-if="eventRecord.status === 1" 
            type="primary" 
            class="action-btn"
            @click="handleCompleteReview"
            :loading="reviewLoading">
            <a-icon type="check" /> 验收通过
          </a-button>
          <a-button 
            v-if="eventRecord.status === 1" 
            class="action-btn action-btn-danger"
            @click="handleReject"
            :loading="rejectLoading">
            <a-icon type="close" /> 驳回
          </a-button>
          <a-button 
            v-if="eventRecord.status === 0 || eventRecord.status === 4" 
            type="primary" 
            class="action-btn"
            @click="showHandleModal">
            <a-icon type="edit" /> {{ eventRecord.status === 4 ? '重新提交处理' : '提交处理' }}
          </a-button>
        </div>

        <!-- Handle记录Timeline -->
        <div v-if="eventRecord.handles && eventRecord.handles.length > 0" class="timeline-section">
          <div class="section-title">处理记录</div>
          <div class="timeline">
            <div 
              v-for="handle in sortedHandles" 
              :key="handle.id"
              class="timeline-item">
              <div class="timeline-dot"></div>
              <div class="timeline-content">
                <div class="timeline-header">
                  <span class="timeline-user">
                    <a-icon type="user" class="user-icon" />
                    {{ handle.user_name || '未知用户' }}
                  </span>
                  <span class="timeline-time">{{ handle.created_at }}</span>
                </div>
                <div v-if="handle.description" class="timeline-desc">{{ handle.description }}</div>
                <div v-if="handle.files && handle.files.length > 0" class="timeline-files">
                  <a-icon type="paper-clip" class="file-icon" />
                  <span v-for="(file, index) in handle.files" :key="index" class="file-name">
                    {{ file }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </a-spin>

    <!-- 提交处理弹窗 -->
    <a-modal
      title="提交处理"
      :visible="handleModalVisible"
      :width="500"
      @ok="submitHandle"
      @cancel="handleModalVisible = false"
      :confirmLoading="handleLoading"
      :bodyStyle="{ padding: '20px' }">
      <a-form layout="vertical">
        <a-form-item label="情况说明">
          <a-textarea v-model="handleForm.description" placeholder="请输入情况说明" :rows="4" />
        </a-form-item>
        <a-form-item label="文件">
          <a-upload
            :file-list="handleFileList"
            @change="handleFileChange"
            :before-upload="beforeUpload">
            <a-button> <a-icon type="upload" /> 上传文件 </a-button>
          </a-upload>
        </a-form-item>
      </a-form>
    </a-modal>
  </a-modal>
</template>

<script>
export default {
  name: 'EventDetailModal',
  data() {
    return {
      visible: false,
      loading: false,
      reviewLoading: false,
      rejectLoading: false,
      handleLoading: false,
      handleModalVisible: false,
      eventRecord: null,
      handleForm: {
        description: '',
        files: []
      },
      handleFileList: []
    }
  },
  computed: {
    // 按时间倒序排列的handle记录
    sortedHandles() {
      if (!this.eventRecord || !this.eventRecord.handles) return []
      return [...this.eventRecord.handles].sort((a, b) => {
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      })
    },
    // 是否可以操作
    canOperate() {
      if (!this.eventRecord) return false
      // 待处理、待验收、已驳回状态可以操作
      return this.eventRecord.status === 0 || this.eventRecord.status === 1 || this.eventRecord.status === 4
    }
  },
  methods: {
    // 打开弹窗
    async open(id) {
      this.visible = true
      await this.loadEventDetail(id)
    },

    // 加载事件详情
    async loadEventDetail(id) {
      this.loading = true
      try {
        const res = await this.$api.get_event_record(id)
        if (res.code === 200) {
          this.eventRecord = res.data
        } else {
          this.$message.error(res.message || '获取事件详情失败')
        }
      } catch (error) {
        console.error('获取事件详情失败:', error)
        this.$message.error('获取事件详情失败')
      } finally {
        this.loading = false
      }
    },

    // 获取状态样式类
    getStatusClass(record) {
      if (record.should_handle === 0) return 'status-badge-default'
      switch (record.status) {
        case 0: return 'status-badge-pending'
        case 1: return 'status-badge-review'
        case 2: return 'status-badge-success'
        case 3: return 'status-badge-cancel'
        case 4: return 'status-badge-rejected'
        default: return 'status-badge-default'
      }
    },

    // 获取状态文本
    getStatusText(record) {
      if (record.should_handle === 0) return '无需处理'
      const statusMap = {
        0: '待处理',
        1: '待验收',
        2: '已完成',
        3: '已取消',
        4: '已驳回'
      }
      return statusMap[record.status] || '未知'
    },

    // 验收完成
    async handleCompleteReview() {
      this.reviewLoading = true
      try {
        const res = await this.$api.complete_event_review(this.eventRecord.id)
        if (res.code === 200) {
          this.$message.success('验收通过')
          this.$emit('review-complete', res.data)
          await this.loadEventDetail(this.eventRecord.id)
        } else {
          this.$message.error(res.message || '验收失败')
        }
      } catch (error) {
        console.error('验收失败:', error)
        this.$message.error('验收失败')
      } finally {
        this.reviewLoading = false
      }
    },

    // 驳回
    async handleReject() {
      this.rejectLoading = true
      try {
        const res = await this.$api.reject_event_review(this.eventRecord.id, {})
        if (res.code === 200) {
          this.$message.success('已驳回')
          this.$emit('review-rejected', res.data)
          await this.loadEventDetail(this.eventRecord.id)
        } else {
          this.$message.error(res.message || '驳回失败')
        }
      } catch (error) {
        console.error('驳回失败:', error)
        this.$message.error('驳回失败')
      } finally {
        this.rejectLoading = false
      }
    },

    // 显示提交处理弹窗
    showHandleModal() {
      this.handleForm = {
        description: '',
        files: []
      }
      this.handleFileList = []
      this.handleModalVisible = true
    },

    // 文件上传前处理
    beforeUpload(file) {
      return false
    },

    // 文件变化处理
    handleFileChange({ fileList }) {
      this.handleFileList = fileList
    },

    // 提交处理
    async submitHandle() {
      this.handleLoading = true
      try {
        const data = {
          handleable_type: 'event_record',
          handleable_id: this.eventRecord.id,
          description: this.handleForm.description,
          files: this.handleFileList.map(f => f.name)
        }
        const res = await this.$api.create_handle(data)
        if (res.code === 200) {
          this.$message.success('处理提交成功')
          this.handleModalVisible = false
          await this.loadEventDetail(this.eventRecord.id)
          this.$emit('handle-created', res.data)
        } else {
          this.$message.error(res.message || '提交失败')
        }
      } catch (error) {
        console.error('提交处理失败:', error)
        this.$message.error('提交失败')
      } finally {
        this.handleLoading = false
      }
    },

    // 取消
    onCancel() {
      this.visible = false
      this.eventRecord = null
    }
  }
}
</script>

<style scoped>
.event-detail {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
}

/* 状态卡片 */
.status-card {
  background: #f5f6f7;
  border-radius: 12px;
  padding: 16px 20px;
  margin-bottom: 20px;
}

.status-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.status-label {
  font-size: 13px;
  color: #65676b;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
}

.status-badge-default {
  background: #e4e6eb;
  color: #4a4a4a;
}

.status-badge-pending {
  background: #fff3cd;
  color: #856404;
}

.status-badge-review {
  background: #cce5ff;
  color: #004085;
}

.status-badge-success {
  background: #d4edda;
  color: #155724;
}

.status-badge-cancel {
  background: #e2e3e5;
  color: #383d41;
}

.status-badge-rejected {
  background: #f8d7da;
  color: #721c24;
}

.status-meta {
  display: flex;
  gap: 24px;
}

.meta-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.meta-label {
  font-size: 12px;
  color: #8a8d91;
}

.meta-value {
  font-size: 14px;
  color: #1c1e21;
  font-weight: 500;
}

/* 信息区域 */
.info-section,
.process-section {
  margin-bottom: 20px;
}

.section-title {
  font-size: 15px;
  font-weight: 600;
  color: #1c1e21;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e4e6eb;
}

.info-row {
  display: flex;
  align-items: flex-start;
  padding: 10px 0;
  border-bottom: 1px solid #f0f2f5;
}

.info-row:last-child {
  border-bottom: none;
}

.info-label {
  width: 120px;
  flex-shrink: 0;
  font-size: 13px;
  color: #65676b;
}

.info-value {
  font-size: 14px;
  color: #1c1e21;
}

.text-warning {
  color: #e67e22;
  font-weight: 500;
}

.text-muted {
  color: #65676b;
}

/* 操作按钮 */
.action-section {
  display: flex;
  gap: 12px;
  padding: 16px 0;
  border-top: 1px solid #e4e6eb;
  border-bottom: 1px solid #e4e6eb;
  margin-bottom: 20px;
}

.action-btn {
  flex: 1;
  height: 42px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  border: none;
}

.action-btn-danger {
  background: #e4e6eb;
  color: #e74c3c;
}

.action-btn-danger:hover {
  background: #d8dadf;
  color: #c0392b;
}

/* Timeline */
.timeline-section {
  margin-top: 20px;
}

.timeline {
  position: relative;
  padding-left: 24px;
}

.timeline-item {
  position: relative;
  padding-bottom: 20px;
  padding-left: 20px;
}

.timeline-item:last-child {
  padding-bottom: 0;
}

.timeline-item::before {
  content: '';
  position: absolute;
  left: 0;
  top: 12px;
  bottom: 0;
  width: 2px;
  background: #e4e6eb;
}

.timeline-item:last-child::before {
  display: none;
}

.timeline-dot {
  position: absolute;
  left: -5px;
  top: 6px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #1877f2;
  border: 2px solid #fff;
  box-shadow: 0 0 0 2px #1877f2;
}

.timeline-content {
  background: #f5f6f7;
  border-radius: 12px;
  padding: 12px 16px;
}

.timeline-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.timeline-user {
  font-size: 14px;
  font-weight: 600;
  color: #1c1e21;
  display: flex;
  align-items: center;
  gap: 6px;
}

.user-icon {
  color: #65676b;
}

.timeline-time {
  font-size: 12px;
  color: #8a8d91;
}

.timeline-desc {
  font-size: 14px;
  color: #1c1e21;
  margin-bottom: 8px;
  line-height: 1.5;
}

.timeline-files {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #1877f2;
}

.file-icon {
  color: #65676b;
}

.file-name {
  padding: 4px 8px;
  background: #e7f3ff;
  border-radius: 4px;
}
</style>
